import idx from 'idx';
import * as React from 'react';
import { mountModal } from 'lib/modal';
import { debounce } from 'lodash';
import AsyncSelect from 'components/AsyncSelect';
import AsyncSelectItem from 'components/AsyncSelectItem';
import OrganizationPickerItem from './OrganizationPickerItem';
import OrganizationPickerNewItem from './OrganizationPickerNewItem';
// @ts-ignore
import { OrganizationCreateModal } from 'organizations/components/OrganizationCreate';
import OrganizationPickerSelectedItem from './OrganizationPickerSelectedItem';

// @ts-ignore
import { Organization } from '../types';

type Props = {
  onChange?: (organization?: Organization) => void;
  onClickInteraction?: () => void;
  onQueryChange: (input: string) => Promise<Organization[]>;
  permanentItem?: any;
  permanentItemOnClick?: () => void;
  initialOrganization?: Organization | null;
  onError?: (e: any) => void;
};

type State = {
  selectedOrganization?: Organization;
  editing: boolean;
  searchString: string;
};

export default class OrganizationPickerView extends React.Component<Props, State> {
  state: State = {
    editing: false,
    searchString: '',
  };

  componentWillMount() {
    // set the selected organization when
    // the initial organization was already in apollo's cache
    if (this.props.initialOrganization) {
      this.setState({ selectedOrganization: this.props.initialOrganization });
    }
  }

  componentWillUpdate(nextProps: Props, nextState: State) {
    const currentID = idx(this.state, _ => _.selectedOrganization.id);
    const nextID = idx(nextState, _ => _.selectedOrganization.id);

    if (currentID !== nextID) {
      if (this.props.onChange) {
        this.props.onChange(nextState.selectedOrganization);
      }
    }

    // set the selected organization when
    // the apollo response came back for the initial organization
    if (!this.props.initialOrganization && nextProps.initialOrganization) {
      this.setState({ selectedOrganization: nextProps.initialOrganization });
    }
  }

  asyncSelect: any;

  /* The search function for the async select.
   * It queries the provider in search for organization matching the query string.
   * Should always be debounced to avoid making too many useless requests.
   */
  search = debounce((input: string, callback: Function) => {
    this.setState({ searchString: input });
    callback(
      this.props.onQueryChange(input)
        .then((organizations: Organization[]) => {
          return organizations.map(o => (
            <OrganizationPickerItem organization={o} key={o.id} />
          ));
        })
        .catch(e => {
          console.error(e);
        }),
    );
  }, 300);

  /* When called, display the organization create modal.
   * When the organization is created we set the selected organization
   * to the new organization.
   */
  mountOrganizationCreateModal = () => {
    const onCreate = (organization: Organization) => {
      this.setState({ selectedOrganization: organization });
    };

    mountModal(
      <OrganizationCreateModal
        onCreate={onCreate}
        organizationType={undefined}
        defaultOrganization={this.state.searchString}
      />,
      this,
    );
  };

  /* Handle clicks of items in the async select dropdown.
   * If the selected item is the permanent item about create a new
   * organization, we display the creation modal, otherwise we set the
   * selected item and exit the editing mode (in case we were in edit mode).
   */
  handleSelect = (element: any) => {
    if (this.props.permanentItemOnClick && element.key === 'permanent_item') {
      this.props.permanentItemOnClick();
    } else if (element.key === 'create_organization') {
      this.mountOrganizationCreateModal();
    } else if (!(element.key === 'permanent_item') && !(element.key === 'create_organization')) {
      this.setState({ selectedOrganization: element.props.organization, editing: false });
    }
  };

  /* When the user clicks "change" on the selected organization,
   * we switch to edit mode, which will cause the async select input to show up.
   * When that is done, we focus it
   */
  handleChange = () => {
    this.setState({ editing: true }, () => this.asyncSelect.focus());

    // clicked on "change" link
    if (this.props.onClickInteraction) {
      this.props.onClickInteraction();
    }
  };

  /* On blur exit the edit mode
   * This will cause the previously selected
   * organization to show up if there was any selected organization.
   */
  handleBlur = () => {
    if (this.state.editing) {
      this.setState({ editing: false });
    }
  };

  render() {
    if (this.state.selectedOrganization && !this.state.editing) {
      return (
        <OrganizationPickerSelectedItem
          onChange={this.handleChange}
          organization={this.state.selectedOrganization}
        />
      );
    } else {
      return (
        <AsyncSelect
          placeholder="Search for an organization"
          permanentItems={
            this.props.permanentItem
              ? [
                <AsyncSelectItem title="" key="permanent_item">
                  {this.props.permanentItem}
                </AsyncSelectItem>,
              ]
              : [<OrganizationPickerNewItem key="create_organization" />]
          }
          ref={a => (this.asyncSelect = a)}
          searchCallback={this.search}
          onSelect={this.handleSelect}
          onBlur={this.handleBlur}
          onClick={this.props.onClickInteraction}
        />
      );
    }
  }
}
